<template>
  <div>
    <ProgressBar active-step="Tell us about your job" />
    <PageHeading
      :default-heading="pd_content.page_header.default"
      :edit-heading="pd_content.page_header.edit"
    />
    <form
      novalidate="novalidate"
      @submit.prevent
    >
      <div class="pvd-container container-divider">
        <h2 class="pvd-heading-L">
          {{ pd_content.are_you_employed_header }}
        </h2>
        <apx-select
          id="employmentStatus"
          :label="pd_content.employment_status_label"
          :error-text="pd_content.employment_status_error"
          :value="pu_employmentStatus"
          required
        />
      </div>
      <div
        v-show="showEmployerDetailsSection"
        class="pvd-container container-divider"
      >
        <h2 class="pvd-heading-L">
          {{ pd_content.employment_details.section_heading_label }}
        </h2>
        <apx-text-input
          id="employerOccupation"
          v-observe-visibility="occupationVisible"
          :value="pu_occupation"
          id-prefix="employerOccupation"
          :label="pd_content.employment_details.your_occupation_label"
          :track-events="true"
        />
        <apx-text-input
          id="companyName"
          v-observe-visibility="companyNameVisible"
          :value="pu_employersName"
          id-prefix="companyName"
          :label="pd_content.employment_details.company_name_label"
          :track-events="true"
        />
        <apx-toggle
          v-show="pu_employmentStatus === 'S'"
          id="toggleEmployerAddrSame"
          :yes-label="pd_content.employment_details.yes_toggle_label"
          :no-label="pd_content.employment_details.no_toggle_label"
          :value="pu_employerAddressSameAsLegal"
          :track-events="true"
          :group-label="pd_content.employment_details.is_company_addr_same_label"
        />
        <div
          v-show="pu_employerAddressSameAsLegal === 'Yes' && pu_employmentStatus === 'S'"
          class="pvd-display-row"
        >
          <div class="display-row-label">
            <p>{{ pd_content.employment_details.company_address_label }}</p>
          </div>
          <div class="display-row-content">
            <p>{{ pu_leg_line1 }} {{ pu_leg_line2 }}</p>
            <p>{{ pu_leg_city }}, {{ pu_leg_state }} {{ pu_leg_zip }}</p>
          </div>
        </div>
        <apx-address
          v-show="showAddressSection"
          id="address"
          :address-line1="pu_emp_line1"
          :address-line2="pu_emp_line2"
          :city-name="pu_emp_city"
          :state-code="pu_emp_state"
          :zip-code="pu_emp_zip"
          :address-labels="JSON.stringify(addressLabels)"
          required
        />
      </div>
      <div
        v-if="showAssociations"
        id="associations"
        class="pvd-container container-divider"
        style="display: block;"
      >
        <h2 class="pvd-heading-L">
          {{ pd_content.associations_section_header }}
        </h2>
        <div
          v-if="showCompanyNameSection"
          style="display: block;"
        >
          <apx-toggle
            v-if="showAssociationToggle"
            id="toggleAssociation"
            v-observe-visibility="associationToggleBecameVisible"
            id-prefix="toggleAssociation"
            stack
            :value="pu_coAffillCode"
            :track-events="true"
            :required-message="pd_content.toggle_text_association"
            :group-label="pd_content.association_information_section_header"
            :help-header="pd_content.associations_modal_header"
            :help-body="pd_content.associations_modal_body"
            help-size="small"
          />
          <div
            v-if="showCompanyName"
            id="association1"
            style="display: block;"
          >
            <apx-autocomplete
              id="companyNameInput"
              v-observe-visibility="companyNameBecameVisible"
              :autocomplete-label="pd_content.association_label"
              :selected-value="pu_coAffillName"
            />
            <div class="pvd-form-row nomargin">
              <div class="form-row-supplemental">
                <p class="xsmall">
                  {{ pd_content.association_supplemental_text }}
                </p>
              </div>
            </div>
          </div>
          <div class="pvd-form-row">
            <div class="form-row-label-group toggle-button-stacked">
              <label>{{ pd_content.control_person_question.question1 }} <a
                id="tt-popover-trigger"
                href="javascript:void(0);"
                @click="track('any_of_your_relatives', 'interaction')"
              >{{ pd_content.control_person_question.question_link }}
              </a> {{ pd_content.control_person_question.question2 }}</label>
            </div>
            <ToggleComponent
              :id-prefix="'controlPersonToggle'"
              :toggle-value="pu_coDirectorCode"
              :yes-label="pd_content.yes_toggle_text"
              :no-label="pd_content.no_toggle_text"
              :track-events="true"
              :validation-rules="pd_content.control_person_validation_rules"
              :message="pd_content.toggle_text_association"
              :conditional-class="'form-row-content'"
              @update:toggleValue="controlPersonToggleChanged"
            />
          </div>
          <ModalComponent
            id-val="tt-popover"
            :modal-body1="pd_content.control_person_modal_body"
            modal-class="small"
          />
          <apx-autocomplete
            v-if="showControlPersonInput"
            id="controlPersonInput"
            v-observe-visibility="cpBecameVisible"
            :selected-value="pu_coDirectorSymbol"
            :autocomplete-label="pd_content.trading_or_cusip_label"
            start-auto-suggest="1"
          />
        </div>
      </div>
      <NavigationComponent
        @next-buttton-clicked="nextButtonClicked"
      />
    </form>
  </div>
</template>

<script>

import Vue from 'vue';
import VueObserveVisibility from 'vue-observe-visibility';
import NavigationComponent from '../components/NavigationComponent.vue';
import ToggleComponent from '../components/ToggleComponent.vue';
import ModalComponent from '../components/ModalComponent.vue';
import PageHeading from '../components/PageHeading.vue';
import ProgressBar from '../components/ProgressBar.vue';
import tradingSymbolAutosuggest from '../mixins/tradingSymbolAutosuggest';
import dataHelper from '../mixins/dataHelper';
import dataSanitize from '../mixins/dataSanitize';
import measurement from '../mixins/measurement';
import utilityFunctions from '../mixins/utility';
import validationHelper from '../mixins/validationHelper';
import addressAutocomplete from '../mixins/addressAutocomplete';
import common from '../mixins/common';

Vue.use(VueObserveVisibility);

const changeEventName = 'value-changed';

export default {
  name: 'EmploymentInformation',
  components: {
    ProgressBar,
    PageHeading,
    NavigationComponent,
    ToggleComponent,
    ModalComponent,
  },
  mixins: [tradingSymbolAutosuggest, dataHelper, dataSanitize, measurement, utilityFunctions,
    validationHelper, addressAutocomplete],
  data() {
    return {
      backButtonAttempt: false,
      mapObject: {},
    };
  },
  computed: {
    showAssociations() {
      return this.pu_employmentStatus !== '';
    },
    showEmployerDetailsSection() {
      const employerDetailsSection = ['E', 'S', 'Z'];
      return employerDetailsSection.includes(this.pu_employmentStatus);
    },
    showCompanyNameSection() {
      return this.showAssociations;
    },
    showCompanyName() {
      return this.pu_coAffillCode === 'Yes';
    },
    showAddressSection() {
      const employerDetailsSection = ['E', 'Z'];
      const visible = (this.pu_employerAddressSameAsLegal === 'No' && this.pu_employmentStatus === 'S')
       || employerDetailsSection.includes(this.pu_employmentStatus);

      if (visible) {
        this.configureAddressSection(this.buildRules('address'));
      } else {
        this.configureAddressSection(common.optionalAddress);
      }
      return visible;
    },
    showAssociationToggle() {
      return this.pu_employmentStatus !== 'Z';
    },
    showControlPersonToggle() {
      return this.pu_employmentStatus === 'Z';
    },
    showControlPersonInput() {
      return this.pu_coDirectorCode === 'Yes';
    },
    addressLabels() {
      return {
        addressLine1: this.pd_content.employment_details.address_line1_label,
        addressLine2: this.pd_content.employment_details.address_line2_label,
        cityName: this.pd_content.employment_details.city_label,
        stateCode: this.pd_content.employment_details.state_label,
        zipCode: this.pd_content.employment_details.zip_code_label,
      };
    },
  },
  created() {
    if (this.cs_editMode) {
      this.unsubscribe = this.sanitizeInstanceData('primaryUser');
    }
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
  mounted() {
    this.trackEdit(this.cs_editMode, 'edit-employment-information');
    this.updatePageTitle();
    this.removeModalClassIfExists();
    if (this.routeId === 'brokerage' || this.routeId === 'fcma') {
      this.configureBackButtonFunctionality(() => this.showBackWarning(
        this.pd_content.back_button_disabled_warning,
      ));
    } else {
      this.configureBackButtonFunctionality(() => this.$emit('invoke-previous'));
    }

    this.populateEmploymentStatuses('#employmentStatus');
    document.querySelector('#employmentStatus').addEventListener(changeEventName, this.employmentStatusChanged);
    document.querySelector('#employerOccupation').addEventListener(changeEventName, (event) => this.employerDetailsChanged(event, 'occupation'));
    document.querySelector('#companyName').addEventListener(changeEventName, (event) => this.employerDetailsChanged(event, 'employersName'));
    this.configureEmployerAddrSameToggle();
    this.initGoogleAutocomplete('address-addressLine1', this.fillAddressValues);
  },
  updated() {
    this.updatePageTitle();
  },
  methods: {
    updatePageTitle() {
      const pageHeader = this.pd_content.page_header;
      if (pageHeader) {
        let heading = this.cs_editMode
          ? pageHeader.edit : pageHeader.default;
        if (!pageHeader.edit) {
          heading = pageHeader.default;
        }
        if (heading) {
          document.title = this.setTitle(heading);
        }
      }
    },
    nextButtonClicked() {
      if (!this.showAddressSection) {
        this.addressChanged({
          detail: {
            address: {
              addressLine1: '', addressLine2: '', cityName: '', stateCode: '', zipCode: '',
            },
          },
        });
      }
      if (this.pu_employmentStatus !== 'S') {
        this.commitPrimaryUserData('', 'employerAddressSameAsLegal');
      }
    },
    configureEmployerAddrSameToggle() {
      const toggleEmployerAddrSame = document.querySelector('#toggleEmployerAddrSame');
      if (toggleEmployerAddrSame) {
        if (this.pu_employmentStatus === 'N' || this.pu_employmentStatus === 'R'
         || this.pu_employerAddressSameAsLegal === '' || this.pu_employerAddressSameAsLegal === 'Yes') {
          this.commitPrimaryUserData('Yes', 'employerAddressSameAsLegal');
          this.configureAddressSection(common.optionalAddress);
        } else if (this.pu_employerAddressSameAsLegal === 'No') {
          this.configureAddressSection(this.buildRules('address'));
        }

        toggleEmployerAddrSame
          .addEventListener(changeEventName, this.employerAddressSameToggleChanged);
        const observer = new MutationObserver(() => {
        });
        const config = {
          attributes: false,
          childList: true,
          characterData: false,
        };
        observer.observe(toggleEmployerAddrSame, config);
      }
    },
    configureAddressSection(rules) {
      const addressComponent = document.querySelector('#address');
      if (addressComponent) {
        addressComponent.removeEventListener(changeEventName, this.addressChanged);
        addressComponent.addEventListener(changeEventName, this.addressChanged);
        addressComponent.validationRules = rules;
      }
    },
    employerAddressSameToggleChanged(event) {
      const { detail } = event;
      this.commitPrimaryUserData(detail, 'employerAddressSameAsLegal');
      if (this.pu_employerAddressSameAsLegal === 'Yes') {
        this.track('employerAddressSame-Yes', 'interaction');
        this.configureAddressSection(common.optionalAddress);
      } else {
        this.track('employerAddressSame-No', 'interaction');
      }
    },
    fillAddressValues(address) {
      const event = {
        detail: {
          address,
        },
      };

      const line1Element = document.getElementById('address-addressLine1');
      line1Element.value = event.detail.address.addressLine1;
      line1Element.dispatchEvent(new Event('change'));

      document.getElementById('address-addressLine2').value = event.detail.address.addressLine2;

      const cityNameElement = document.getElementById('address-cityName');
      cityNameElement.value = event.detail.address.cityName;
      cityNameElement.dispatchEvent(new Event('change'));

      const stateCodeElement = document.getElementById('address-stateCode');
      stateCodeElement.value = event.detail.address.stateCode;
      stateCodeElement.dispatchEvent(new Event('change'));

      const zipCodeElement = document.getElementById('address-zipCode');
      zipCodeElement.value = event.detail.address.zipCode;
      zipCodeElement.dispatchEvent(new Event('change'));

      this.addressChanged(event);
    },
    associationToggleBecameVisible(isVisible) {
      const associationToggle = document.querySelector('#toggleAssociation');
      if (isVisible) {
        associationToggle.addEventListener(changeEventName, this.associationToggleChanged);
        document.getElementById('toggleAssociation-modal-trigger').onclick = () => { this.track('association_help', 'interaction'); };
        this.fixApexToggleAlignmment('toggleAssociation');
      }
    },
    tradingSymbolCallBack(tradingLookupResp, symb) {
      const controlPersonInput = document.querySelector('#controlPersonInput');
      this.mapObject = tradingLookupResp;
      this.populateAssociationList('#controlPersonInput', this.mapObject);
      const searchKey = symb;
      controlPersonInput.dispatchEvent(
        new CustomEvent('listen-for-search-results', {
          bubbles: true,
          composed: true,
          detail: {
            searchResults: controlPersonInput.listOfItems,
            searchKey,
            isFuzzy: false,
          },
        }),
      );
    },
    cpBecameVisible(isVisible) {
      const controlPersonInput = document.querySelector('#controlPersonInput');
      if (isVisible) {
        controlPersonInput.validationText.autocomplete.required = this
          .pd_content.trading_or_cusip_validation_text.required;
        controlPersonInput.validationText.autocomplete.min = this
          .pd_content.trading_or_cusip_validation_text.min;
        controlPersonInput.validationText.autocomplete.regex = this
          .pd_content.trading_or_cusip_validation_text.regex;
        controlPersonInput.validationRules = this.buildRules('controlPerson');
        controlPersonInput.listOfItems = [];
        controlPersonInput.addEventListener(changeEventName, this.controlPersonValueChanged);
        controlPersonInput.addEventListener('dispatch-search-key', (evt) => {
          this.tradingSymbolLookup(evt.detail, this.tradingSymbolCallBack);
        });
      }
    },
    employmentStatusChanged(event) {
      const value = event.detail.selectValue;
      let companyName = '';
      if (value === 'Z') {
        this.commitPrimaryUserData('', 'coAffillName');
        this.commitPrimaryUserData('', 'coAffillCode');
        companyName = this.pd_content.employment_details.fidelity_investments_label;
      }

      if (this.pu_employmentStatus === 'Z' || value === 'Z') {
        this.commitPrimaryUserData(companyName, 'employersName');
        document.querySelector('#companyName input').value = companyName;
      }

      this.commitPrimaryUserData(value, 'employmentStatus');
    },
    employerDetailsChanged(event, fieldProperty) {
      this.commitPrimaryUserData(event.detail.value, fieldProperty);
    },

    companyNameVisible(isVisible) {
      const companyNameComponent = document.querySelector('#companyName');
      const companyNameInput = document.querySelector('#companyName input');
      const employerDetailsSection = ['E', 'S', 'Z'];
      if (isVisible) {
        companyNameComponent.validationText.validations.required = this
          .pd_content.company_name_validation_text.required;
        companyNameComponent.validationText.validations.min = this
          .pd_content.company_name_validation_text.required;
        const validationRules = this.buildRules('companyName').employerName;
        companyNameComponent.validationRules.validations = validationRules;
        companyNameInput.minlength = validationRules.min;
        companyNameInput.maxlength = validationRules.max;
        companyNameInput.type = 'text';
      } else if (!employerDetailsSection.includes(this.pu_employmentStatus)) {
        companyNameComponent.validationRules.validations = common.optionalCommonValue;
        companyNameInput.minlength = common.optionalCommonValue.min;
        companyNameInput.maxlength = common.optionalCommonValue.max;
      }
    },

    occupationVisible(isVisible) {
      const occupationComponent = document.querySelector('#employerOccupation');
      const occupationInput = document.querySelector('#employerOccupation input');
      const employerDetailsSection = ['E', 'S', 'Z'];
      if (isVisible) {
        occupationComponent.validationText.validations.required = this
          .pd_content.occupation_validation_text.required;
        occupationComponent.validationText.validations.min = this
          .pd_content.occupation_validation_text.required;
        const validationRules = this.buildRules('occupation').occupation;
        occupationComponent.validationRules.validations = validationRules;
        occupationInput.minlength = validationRules.min;
        occupationInput.maxlength = validationRules.max;
        occupationInput.type = 'text';
      } else if (!employerDetailsSection.includes(this.pu_employmentStatus)) {
        occupationComponent.validationRules.validations = common.optionalCommonValue;
        occupationInput.minlength = common.optionalCommonValue.min;
        occupationInput.maxlength = common.optionalCommonValue.max;
      }
    },

    controlPersonToggleChanged(toggleValue) {
      this.commitPrimaryUserData(toggleValue, 'coDirectorCode');
      const controlPersonInput = document.querySelector('#controlPersonInput');
      if (controlPersonInput && toggleValue === 'No') {
        controlPersonInput.removeEventListener(changeEventName, this.controlPersonValueChanged);
      }
      this.pu_coDirectorCode = toggleValue;
    },
    controlPersonValueChanged(event) {
      this.commitPrimaryUserData(event.detail.value, 'coDirectorSymbol');
      const cusipId = this.populateIdFromMap(event.detail.value, this.mapObject);
      this.commitPrimaryUserData(cusipId, 'cusip');
    },
    associationToggleChanged(event) {
      const { detail } = event;
      this.commitPrimaryUserData(detail, 'coAffillCode');
      const companyNameInput = document.querySelector('#companyNameInput');
      if (companyNameInput && detail === 'No') {
        companyNameInput.removeEventListener(changeEventName, this.companyNameValueChanged);
      }
      if (event.detail === 'No') {
        this.track('association_toggle_No', 'interaction');
      } else {
        this.track('association_toggle_Yes', 'interaction');
      }
    },
    companyNameValueChanged(event) {
      this.commitPrimaryUserData(event.detail.value, 'coAffillName');
      const affId = this.populateIdFromMap(event.detail.value, this.pd_affiliations);
      this.commitPrimaryUserData(affId, 'coAffillId');
    },
    addressChanged(event) {
      this.commitPrimaryUserData(event.detail.address.addressLine1, 'addresses.employer.line1');
      this.commitPrimaryUserData(event.detail.address.addressLine2, 'addresses.employer.line2');
      this.commitPrimaryUserData(event.detail.address.cityName, 'addresses.employer.city');
      this.commitPrimaryUserData(event.detail.address.stateCode, 'addresses.employer.state');
      this.commitPrimaryUserData(event.detail.address.zipCode, 'addresses.employer.zip');
    },
    companyNameBecameVisible(isVisible) {
      if (isVisible) {
        const autoCompleteElement = document.querySelector('#companyNameInput');
        this.populateAssociationList('#companyNameInput', this.pd_affiliations);
        autoCompleteElement.validationText.autocomplete.min = this
          .pd_content.association_country_validation_text.min;
        autoCompleteElement.validationText.autocomplete.regex = this
          .pd_content.association_country_validation_text.regex;
        autoCompleteElement.validationText.autocomplete.required = this
          .pd_content.association_country_validation_text.required;
        autoCompleteElement.validationRules = this.buildRules('affiliation');
        autoCompleteElement.addEventListener(changeEventName, this.companyNameValueChanged);
        autoCompleteElement.addEventListener('dispatch-search-key', (evt) => {
          const searchKey = evt.detail;
          autoCompleteElement.dispatchEvent(
            new CustomEvent('listen-for-search-results', {
              bubbles: true,
              composed: true,
              detail: {
                searchResults: autoCompleteElement.listOfItems,
                searchKey,
                isFuzzy: false,
              },
            }),
          );
        });
      }
    },
  },

};
</script>

<style scoped>
</style>
